import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Rating from '../../Generic/Rating';
import urlProvider from '../../../utils/url-manager/urlProvider';
import cardStyles from './card-tw-styles';
import { getCdnImage as getImageUrl } from '../../../utils/ImageManager';
import { Button } from '../../Generic/Button';
import bvstyles from '../../Generic/BuyNowLite/BuyNowLite-tw-styles';
import getWidth from '../../../utils/gridWidth';
import { withLocale } from '../../Generic/LocaleProvider';
import { getPrimaryRating } from '../../../services/bazaarvoice-api';
import i18nMessages from '../../../constants/messages/pt-BR';
import DOMPurify from 'isomorphic-dompurify';
import dynamic from 'next/dynamic';

const CardMedia = dynamic(() =>
  import('../../Generic/Card').then(module => module.CardMedia)
);
const Card = dynamic(() => import('../../Generic/Card').then(module => module.Card));
const CardContent = dynamic(() =>
  import('../../Generic/Card').then(module => module.CardContent)
);
const Typography = dynamic(() => import('../../Generic/Typography'));

export class ListingCard extends React.PureComponent {
  state = {
    ratingData: null,
  };

  componentDidMount() {
    this.fetchRatingData();
  }

  averageOverallRatingFormat = avgOverAllRating => {
    if (avgOverAllRating % 1 === 0) {
      return avgOverAllRating;
    }
    return avgOverAllRating.toFixed(2);
  };

  fetchRatingData() {
    const { data, locale } = this.props;
    const productIdList = [];
    const productDetail = data;

    if (productDetail && productDetail.ratingAndReviewId) {
      productIdList.push(productDetail && productDetail.ratingAndReviewId);
      getPrimaryRating(productIdList, locale)
        .then(res => {
          if (res.data.Results.length > 0) {
            this.setState({
              ratingData: res.data.Results[0],
            });
          }
        })
        .catch(err => {
          throw new Error(err);
        });
    }
  }

  /**
   * If any non-button element inside the card receives focus (image/title/stars),
   * immediately redirect focus to the Buy Now button.
   * This guarantees ONLY the button will keep focus.
   */
  redirectFocusToBuyNow = (e, buyNowButtonId) => {
    // Avoid infinite loops if focus is already on Buy Now
    const buyNowEl = document.getElementById(buyNowButtonId);
    if (!buyNowEl) return;

    if (document.activeElement !== buyNowEl) {
      // Stop focus from staying on non-button elements
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      // Move focus to Buy Now
      buyNowEl.focus();
    }
  };

  render() {
    const { cardType, data, locale, handleDialogViewState } = this.props;
    const { ratingData } = this.state;

    const constants = require(`../../../constants/messages/${locale}`);

    const defaultCardTypeStyle = 'productListing';
    const requestedCardTypeStyle =
      (data && data.styles && data.styles.trim()) || cardType || defaultCardTypeStyle;
    const cardTypestyle =
      cardStyles[requestedCardTypeStyle] !== undefined
        ? requestedCardTypeStyle
        : defaultCardTypeStyle;
    const resolvedCardStyle = cardStyles[cardTypestyle] || cardStyles[defaultCardTypeStyle];

    const mediaProps = {
      type: i18nMessages.img,
      src: data.heroImage && data.heroImage.url,
      alt: data.heroImage && data.heroImage.description,
      sizes: data.heroImage && data.heroImage.size,
    };

    let widthClass;
    if (data.componentWidth == null) {
      widthClass = '';
    } else {
      widthClass = getWidth(data.componentWidth);
    }

    // Stable button id used for redirecting focus
    const buyNowButtonId =
      data && data.sys && data.sys.id ? `buyNowBtn-${data.sys.id}` : 'buyNowBtn';

    let cardMedia = '';
    let url = `${data.parentPage && data.parentPage.slug}/${data.slug}`;
    const link = urlProvider(i18nMessages.productPage, locale, url);

    if (mediaProps.src) {
      cardMedia = (
        <CardMedia
          className={classNames(resolvedCardStyle.cardMedia)}
          image={getImageUrl(mediaProps.src, { w: 170 })}
          component={mediaProps.type}
          sizes={mediaProps.sizes}
          alt={mediaProps.alt ? mediaProps.alt : 'img'}
          cardType={cardType}
          width={170}
        />
      );
    }

    let title = '';
    if (data.productName) {
      title = (
        <Typography component="h3" className={resolvedCardStyle.heading}>
          {data.productName}
        </Typography>
      );
    }

    let titleMediaLink = '';
    let cardMediaLink = '';

    // IMPORTANT: Make links NOT tabbable
    if (link.path) {
      cardMediaLink = (
        <a
          data-pgaction-redirection="0"
          href={link.path}
          className={classNames([data.gaEventClass], 'event_image_click')}
          data-action-detail="event_image_click"
          tabIndex={-1}
          onFocus={e => this.redirectFocusToBuyNow(e, buyNowButtonId)}
          aria-hidden="true"
        >
          {cardMedia}
        </a>
      );

      titleMediaLink = (
        <a
          data-pgaction-redirection="0"
          href={link.path}
          className={classNames([data.gaEventClass], 'no-underline', classNames)}
          tabIndex={-1}
          onFocus={e => this.redirectFocusToBuyNow(e, buyNowButtonId)}
          aria-hidden="true"
        >
          {title}
        </a>
      );
    } else {
      cardMediaLink = cardMedia;
      titleMediaLink = title;
    }

    const avgRating =
      ratingData && ratingData.ReviewStatistics
        ? ratingData.ReviewStatistics.AverageOverallRating
        : 0;

    const reviewCount =
      ratingData && ratingData.TotalReviewCount > 0 ? ratingData.TotalReviewCount : 0;

    const formattedRating = avgRating % 1 === 0 ? avgRating : avgRating.toFixed(2);

    return (
      <Card
        key={data.sys.id}
        className={classNames(widthClass, resolvedCardStyle.card)}
      >
        {/* Focus-guard: ANY focus inside this block is redirected to Buy Now */}
        <div onFocusCapture={e => this.redirectFocusToBuyNow(e, buyNowButtonId)}>
          {cardMediaLink}

          <CardContent>
            <div>
              <Rating rating={avgRating} tabIndex={-1} />
            </div>

            {titleMediaLink}
          </CardContent>
        </div>

        {data.productCategoryType && (
          <Typography
            className={classNames(resolvedCardStyle.description)}
            component="div"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(data.productCategoryType),
            }}
          />
        )}

        {handleDialogViewState ? (
          <div id={i18nMessages.buyNowBtnWrapper}>
            <div id={i18nMessages.buyNowBtn} className={bvstyles.listCardBtn}>
              <Button
                id={buyNowButtonId}
                className={bvstyles.buyNowBtn.buyNow}
                data-action-detail={data && data.gtin}
                onClick={() => handleDialogViewState(data)}
                aria-label={`Comprar ${data.productName || ''} ${formattedRating} de 5 estrellas, ${reviewCount} avaliações`}
              >
                {constants.buyNow}
              </Button>
            </div>
          </div>
        ) : (
          ''
        )}
      </Card>
    );
  }
}

ListingCard.propTypes = {
  cardType: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
  locale: PropTypes.string.isRequired,
  handleDialogViewState: PropTypes.func.isRequired,
};

// TODO(next16): defaultProps removed in React 19 for function components.
// Convert to inline parameter defaults: const ListingCard = ({prop = val, cardType = 'productListing' }) =>

export default withLocale(ListingCard);